import React, { useEffect } from 'react';
import * as materialui from '@mui/material';
import styled from 'styled-components';
import { Buttons } from '../layout/Buttons';
import { SubmitButtons } from './SubmitButtons';
import { FormikContext, FormikProps, Form } from 'formik';
import { Loader } from '~/component/common/Loader';

export const MaterialModal = styled(materialui.Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  form {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
  }
`;
export const MaterialDrawer = styled(materialui.Drawer)`
  width: 100%;
  & > .MuiDrawer-paperAnchorRight {
    width: 100%;
    max-width: 900px;
  }
`;

export const DrawerContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  > form {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

export const ModalContainer = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
  background-color: #fff;
  width: 800px;
  position: fixed;
  max-height: calc(100% - 48px);
  > form {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
`;

export const Title = styled.h2`
  font-size: 14px;
`;
export const Header = styled.div`
  padding-bottom: 8px;
  margin: 16px 16px 8px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
`;
export const Body = styled.div`
  overflow: auto;
  flex-grow: 1;
`;
export const ButtonArea = styled(Buttons)`
  margin-top: 8px;
`;
export const Actions = styled.div`
  margin-left: 8px;
  display: flex;
  align-items: center;
  & > * {
    white-space: nowrap;
  }
`

interface ModalProps<T extends object> {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  cancelButtonText?: string;
  submitButtonText?: string;
  className?: string;
  title: string;
  formik?: FormikProps<T>;
  children?: any;
  disabled?: boolean;
  isContentsLoading?: boolean;
  isLoading?: boolean;
  buttons?: any;
  footerButtons?: any;
}

type DialogComponent = <T extends object>(
  props: ModalProps<T>
) => React.ReactElement<ModalProps<T>>;

export const DialogContents: DialogComponent = (props) => {
  const onClose = () => {
    if (props.formik?.isSubmitting) return;
    props.onClose();
    props.formik?.resetForm();
  };

  useEffect(() => {
    if (!props.isOpen && props.formik) {
      props.formik?.resetForm();
    }
  }, [props.isOpen]);

  return (
    <>
      <Header>
        <Title>{props.title}</Title>
        {props.buttons && <Actions>{props.buttons}</Actions>}
      </Header>
      {
        props.isContentsLoading && <Loader />
      }
      {
        !props.isContentsLoading && <>
          {props.formik ? (
            <FormikContext.Provider value={props.formik}>
              <Form>
                <Body>{props.children}</Body>
                <ButtonArea>
                  {props.footerButtons || (
                    <SubmitButtons
                      type="submit"
                      className={props.className}
                      submitButtonText={props.submitButtonText}
                      cancelButtonText={props.cancelButtonText}
                      onCancel={onClose}
                      onClick={props.onSubmit}
                      disabled={props.formik.isSubmitting}
                      isSubmitting={props.formik.isSubmitting}
                    />
                  )}
                </ButtonArea>
              </Form>
            </FormikContext.Provider>
          ) : (
            <>
              <Body>{props.children}</Body>
              <ButtonArea>
                {props.footerButtons || (
                  <SubmitButtons
                    className={props.className}
                    submitButtonText={props.submitButtonText}
                    cancelButtonText={props.cancelButtonText}
                    onCancel={props.onClose}
                    type="button"
                    onClick={props.onSubmit}
                    disabled={props.disabled || props.isLoading}
                    hideTrueButton={!props.onSubmit}
                    isSubmitting={props.isLoading}
                  />
                )}
              </ButtonArea>
            </>
          )}
        </>
      }
    </>
  );
};

export const Modal: DialogComponent = (props) => {
  return (
    <MaterialModal
      open={props.isOpen}
      onClose={() => {
        if (props.formik?.isSubmitting) return;
        props.onClose();
      }}
    >
      <ModalContainer>
        <DialogContents {...props} />
      </ModalContainer>
    </MaterialModal>
  );
};

export const Drawer: DialogComponent = (props) => {
  return (
    <MaterialDrawer
      open={props.isOpen}
      onClose={() => {
        if (props.formik?.isSubmitting) return;
        props.onClose();
      }}
      anchor="right"
    >
      <DrawerContainer>
        <DialogContents {...props} />
      </DrawerContainer>
    </MaterialDrawer>
  );
};
